// ═══════════════════════════════════════════════════════════════════════════
// AI-callable safe-contour guard (CC-2a foundation)
// ═══════════════════════════════════════════════════════════════════════════
//
// Enforces the ТЗ §8 / §10.1 "AI callable" contract at the AI → EditorCommand
// translate/dispatch boundary: the AI Drafting Bot may only execute commands
// whose `CommandDescriptor.aiCallable === true`. This is the runtime half of
// the rule encoded as `aiCallable` on every registry descriptor — it fails
// CLOSED, so an AI proposal that targets a non-AI-callable command (or a
// command that is not in the registry at all) is rejected before any
// EditorCommand reaches the store.
//
// IMPORTANT — why the guard is ACTION-level, not literal-level:
//   A single AI action (e.g. CREATE_DYNAMIC_BLOCK) legitimately expands into
//   several low-level EditorCommands, some of which carry literals owned by
//   descriptors that are themselves NOT directly AI-callable (the block editor
//   session steps `space/enter-block-edit` / `space/exit-block-edit`,
//   `properties/apply`, `chprop/apply`, `geometry/add-dynamic-parameter`, …).
//   Those literals are internal mechanics of the safe contour, not the
//   AI-callable surface. The guard therefore checks the *command the AI is
//   asking for* (the registry descriptor the action targets), never the
//   internal EditorCommand steps the translator emits.
//
// Spec basis:
//   • ТЗ §10.1 "AI callable" column (authoritative per-command matrix)
//   • ТЗ §16 "AI drafting changes без inspection ... не принимаются"
//   • ТЗ §17 "примитив нельзя отмечать как AI-callable for creation, если у
//     него нет полного command-flow и полного property coverage"
//   • CLAUDE.md: "AI drafting ... может только предлагать и исполнять команды
//     через безопасный контур."
// ═══════════════════════════════════════════════════════════════════════════

import {
  ALL_COMMAND_DESCRIPTORS,
  getDescriptorById,
  type CommandDescriptor,
  type CommandRegistryId,
} from "@dxf-ai-editor/command-registry";

import type { BlockAiCommand } from "./blockSchemas";
import type { EntityAiCommand } from "./entitySchemas";

/**
 * Typed error thrown when the AI safe contour is asked to execute a command
 * that is not AI-callable (either marked `aiCallable: false` or absent from
 * the registry entirely). Callers can `instanceof`-narrow this to surface a
 * structured rejection to the AI transcript / audit trail.
 */
export class NonAiCallableCommandError extends Error {
  /** Registry id (or AI action) the rejection is about. */
  readonly commandId: string;
  /** Why the command was rejected. */
  readonly reason: "not-ai-callable" | "unknown-command";

  constructor(commandId: string, reason: "not-ai-callable" | "unknown-command") {
    super(
      reason === "unknown-command"
        ? `AI safe contour: command "${commandId}" is not in the registry; refusing to dispatch (fail-closed).`
        : `AI safe contour: command "${commandId}" is not AI-callable (aiCallable !== true); refusing to dispatch (fail-closed).`,
    );
    this.name = "NonAiCallableCommandError";
    this.commandId = commandId;
    this.reason = reason;
  }
}

/**
 * Non-throwing predicate: is the registry command identified by `commandId`
 * AI-callable? Returns `false` for unknown commands (fail-closed).
 */
export function isAiCallableCommand(
  commandId: CommandRegistryId,
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): boolean {
  const descriptor = getDescriptorById(commandId, descriptors);
  return descriptor !== null && descriptor.aiCallable === true;
}

/**
 * Throwing guard: asserts that `commandId` resolves to a registry descriptor
 * with `aiCallable === true`. Throws `NonAiCallableCommandError` otherwise.
 * Use at the AI translate/dispatch boundary before producing EditorCommands.
 */
export function assertAiCallableCommand(
  commandId: CommandRegistryId,
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): void {
  const descriptor = getDescriptorById(commandId, descriptors);
  if (descriptor === null) {
    throw new NonAiCallableCommandError(commandId, "unknown-command");
  }
  if (descriptor.aiCallable !== true) {
    throw new NonAiCallableCommandError(commandId, "not-ai-callable");
  }
}

/**
 * Maps every block-operation AI action to the user-facing registry command it
 * targets. The guard resolves this id and checks its `aiCallable` flag, so a
 * future action that targets a non-AI-callable command fails closed.
 *
 * `Record<BlockAiCommand["action"], …>` forces exhaustiveness: adding a new AI
 * action without declaring its target command is a compile error.
 *
 * Mapping rationale (see per-command comments in command-registry):
 *   • CREATE_DYNAMIC_BLOCK → BLOCK — it authors a block *definition*; the
 *     dynamic-parameter authoring steps are internal to the safe contour.
 *   • SET_VISIBILITY_STATE / LOOKUP_SELECT / UPDATE_DYNAMIC_PARAMETER → DYNSET
 *     — all three set a dynamic-block property on an *instance*.
 *   • UPDATE_ATTRIBUTE → EATTEDIT; CREATE/UPDATE/RENAME/REMOVE/REORDER ATTDEF
 *     → ATTDEF; PURGE_BLOCKS → PURGE; RENAME_BLOCK → RENAME; EXPLODE → EXPLODE;
 *     SET_CANNOSCALE → CANNOSCALE.
 */
export const AI_ACTION_TARGET_COMMAND: Record<
  BlockAiCommand["action"],
  CommandRegistryId
> = {
  CREATE_BLOCK: "BLOCK",
  INSERT_BLOCK: "INSERT",
  EXPLODE: "EXPLODE",
  UPDATE_ATTRIBUTE: "EATTEDIT",
  CREATE_DYNAMIC_BLOCK: "BLOCK",
  SET_VISIBILITY_STATE: "DYNSET",
  LOOKUP_SELECT: "DYNSET",
  UPDATE_DYNAMIC_PARAMETER: "DYNSET",
  SET_CANNOSCALE: "CANNOSCALE",
  CREATE_ATTDEF: "ATTDEF",
  UPDATE_ATTDEF: "ATTDEF",
  RENAME_ATTDEF_TAG: "ATTDEF",
  REMOVE_ATTDEF: "ATTDEF",
  REORDER_ATTDEFS: "ATTDEF",
  PURGE_BLOCKS: "PURGE",
  RENAME_BLOCK: "RENAME",
};

/**
 * Guard a single AI action: resolves its target registry command and asserts
 * AI-callability. Throws `NonAiCallableCommandError` when the target is not
 * AI-callable (fail-closed). Called by `translateAICommandToDispatch`.
 */
export function assertAiCallableAction(
  action: BlockAiCommand["action"],
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): void {
  assertAiCallableCommand(AI_ACTION_TARGET_COMMAND[action], descriptors);
}

/**
 * Maps every ENTITY-CREATION AI action (CC-2c sub-arm 1) to the user-facing
 * registry command it targets. The guard resolves this id and checks its
 * `aiCallable` flag — so a future entity action wired to a non-AI-callable
 * command (or a command absent from the registry) fails closed.
 *
 * `Record<EntityAiCommand["action"], …>` forces exhaustiveness: adding a new
 * entity action without declaring its target command is a compile error.
 *
 * Mapping rationale:
 *   • CREATE_TEXT → TEXT; CREATE_MTEXT → MTEXT; CREATE_HATCH → HATCH;
 *     CREATE_MLEADER → MLEADER; CREATE_REVCLOUD → REVCLOUD.
 *   • CREATE_DIMENSION → DIMENSION — the single generic DIMENSION descriptor is
 *     the only aiCallable dimension command; the DIM family variants are
 *     selected via the command's `dimensionType`, not separate descriptors.
 */
export const AI_ENTITY_ACTION_TARGET_COMMAND: Record<
  EntityAiCommand["action"],
  CommandRegistryId
> = {
  CREATE_TEXT: "TEXT",
  CREATE_MTEXT: "MTEXT",
  CREATE_HATCH: "HATCH",
  CREATE_DIMENSION: "DIMENSION",
  CREATE_MLEADER: "MLEADER",
  CREATE_REVCLOUD: "REVCLOUD",
};

/**
 * Guard a single ENTITY-CREATION AI action: resolves its target registry
 * command and asserts AI-callability. Throws `NonAiCallableCommandError` when
 * the target is not AI-callable (fail-closed). Called by
 * `translateEntityCommand`.
 */
export function assertAiCallableEntityAction(
  action: EntityAiCommand["action"],
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): void {
  assertAiCallableCommand(AI_ENTITY_ACTION_TARGET_COMMAND[action], descriptors);
}
